import { Suspense, type ReactNode } from 'react';
import { ErrorBoundary } from '@/components/system/ErrorBoundary';
import { RouteFocusReset } from '@/components/system/RouteAnnouncer';
import { PageSkeleton } from './PageSkeleton';
import { FilterUrlSync } from '@/state/FilterUrlSync';
import { SettingsHydrator } from '@/state/SettingsHydrator';
import { CommandPaletteProvider } from '@/components/ui/CommandPalette';
import { RiderOrdersDialogRoot } from '@/components/riders/RiderOrdersDialog';
import { VendorDetailDialogRoot } from '@/components/vendors/VendorDetailDialog';
import { LostItemDetailDialogRoot } from '@/components/inventory/LostItemDetailDialog';
import { AuditDrilldownDialogRoot } from '@/components/inventory/AuditDrilldownDialog';
import { LiveTickerBar } from '@/features/live/LiveTickerBar';
import { useAlertEngine, isAlertEngineEnabled } from '@/features/alerts/engine';
import { CrmEventsSubscriber } from '@/features/crm/CrmEventsSubscriber';
import { FastoSathi } from '@/features/sathi/FastoSathi';
import { Sidebar, MobileSidebarDrawer } from './Sidebar';
import { Topbar } from './Topbar';
import { MobileNavProvider } from './MobileNavContext';

/**
 * Authenticated app shell.
 *
 * Composition order matters:
 *   ErrorBoundary       → catches anything below (incl. Topbar palette)
 *     FilterUrlSync     → needs Router context (provided by RouterProvider)
 *       CmdPaletteCtx   → mounts ⌘K listener & dialog at the root
 *         Sidebar/Topbar/Outlet
 */
export function AppShell({ children }: { children: ReactNode }) {
  // Run the rule engine globally — it no-ops when the user has not enabled alerts.
  useAlertEngine(isAlertEngineEnabled());
  return (
    <ErrorBoundary scope="AppShell">
      <FilterUrlSync>
        <SettingsHydrator />
        <MobileNavProvider>
          <CommandPaletteProvider>
            <RiderOrdersDialogRoot />
            <VendorDetailDialogRoot />
            <LostItemDetailDialogRoot />
            <AuditDrilldownDialogRoot />
            <CrmEventsSubscriber />
            <FastoSathi />
            <MobileSidebarDrawer />
            <RouteFocusReset />
            {/*
             * First stop on a Tab from the address bar. Without it, reaching
             * the page content past ~50 sidebar links takes 50 presses.
             */}
            <a href="#content" className="skip-link">
              Skip to content
            </a>
            <div className="flex min-h-screen w-full">
              <Sidebar />
              <div className="flex min-w-0 flex-1 flex-col">
                <Topbar />
                <LiveTickerBar />
                {/*
                 * `tabIndex={-1}` makes this programmatically focusable so
                 * RouteFocusReset can move focus here after a navigation.
                 */}
                <main
                  id="content"
                  tabIndex={-1}
                  className="flex-1 px-3 py-4 outline-none sm:px-6 sm:py-6 lg:px-8"
                >
                  <div className="mx-auto w-full max-w-[1400px]">
                    <ErrorBoundary scope="Page">
                      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
                    </ErrorBoundary>
                  </div>
                </main>
              </div>
            </div>
          </CommandPaletteProvider>
        </MobileNavProvider>
      </FilterUrlSync>
    </ErrorBoundary>
  );
}
